import { useEffect, useRef, useState, useCallback } from "react";
import * as fabric from "fabric";
import { toast } from "sonner";
import { Upload, Type, Trash2, Copy, ArrowUp, ArrowDown, X, Check, Loader2, Layers, Square } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { GreenButton, GhostButton } from "@/components/Buttons";

const CANVAS = 560;
const FONTS = ["Cabinet Grotesk", "Inter", "JetBrains Mono", "Georgia", "Impact"];
const COLORS = ["#000000", "#ffffff", "#2563EB", "#2563eb", "#ef4444", "#f59e0b", "#a855f7", "#ec4899"];

export default function LiveDesigner({ product, onClose, onUse }) {
    const canvasElRef = useRef(null);
    const fabricRef = useRef(null);
    const fileRef = useRef(null);
    const [selected, setSelected] = useState(null);
    const [busy, setBusy] = useState(false);
    const [sourceFiles, setSourceFiles] = useState([]);
    const isApparel = product.category === "apparel";

    useEffect(() => {
        if (fabricRef.current) return;
        const c = new fabric.Canvas(canvasElRef.current, { width: CANVAS, height: CANVAS, backgroundColor: isApparel ? "#141416" : "#ffffff", preserveObjectStacking: true });
        fabricRef.current = c;

        const printArea = new fabric.Rect({
            left: isApparel ? CANVAS * 0.3 : 20, top: isApparel ? CANVAS * 0.26 : 20,
            width: isApparel ? CANVAS * 0.4 : CANVAS - 40, height: isApparel ? CANVAS * 0.46 : CANVAS - 40,
            fill: "transparent", stroke: isApparel ? "rgba(37, 99, 235,0.6)" : "rgba(0,0,0,0.25)", strokeDashArray: [6, 5],
            selectable: false, evented: false, excludeFromExport: true,
        });

        if (isApparel) {
            fabric.FabricImage.fromURL("/assets/tee-mockup.png").then((img) => {
                const scale = CANVAS / img.width;
                img.set({ scaleX: scale, scaleY: scale, selectable: false, evented: false, excludeFromExport: false });
                c.backgroundImage = img;
                c.add(printArea);
                c.requestRenderAll();
            }).catch(() => { c.add(printArea); c.requestRenderAll(); });
        } else {
            c.add(printArea);
            c.requestRenderAll();
        }

        const sync = () => setSelected(c.getActiveObject() || null);
        c.on("selection:created", sync);
        c.on("selection:updated", sync);
        c.on("selection:cleared", () => setSelected(null));
        return () => { try { c.dispose(); } catch (e) {} fabricRef.current = null; };
    }, [isApparel]);

    const addText = () => {
        const c = fabricRef.current;
        const t = new fabric.IText("Your text", { left: CANVAS / 2, top: CANVAS / 2, originX: "center", originY: "center", fontFamily: "Cabinet Grotesk", fill: isApparel ? "#ffffff" : "#000000", fontSize: 44, fontWeight: 700 });
        c.add(t); c.setActiveObject(t); c.requestRenderAll();
    };

    const addImageFromFile = async (file) => {
        setBusy(true);
        try {
            const url = URL.createObjectURL(file);
            const img = await fabric.FabricImage.fromURL(url);
            const max = CANVAS * 0.42;
            const scale = Math.min(max / img.width, max / img.height, 1);
            img.set({ left: CANVAS / 2, top: CANVAS / 2, originX: "center", originY: "center", scaleX: scale, scaleY: scale });
            fabricRef.current.add(img); fabricRef.current.setActiveObject(img); fabricRef.current.requestRenderAll();
            // upload original print-ready file to storage in background
            const fd = new FormData(); fd.append("file", file);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setSourceFiles((p) => [...p, data]);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const del = () => { const c = fabricRef.current; const o = c.getActiveObject(); if (o) { c.remove(o); c.requestRenderAll(); setSelected(null); } };
    const dup = async () => { const c = fabricRef.current; const o = c.getActiveObject(); if (!o) return; const cl = await o.clone(); cl.set({ left: o.left + 20, top: o.top + 20 }); c.add(cl); c.setActiveObject(cl); c.requestRenderAll(); };
    const forward = () => { const c = fabricRef.current; const o = c.getActiveObject(); if (o) { c.bringObjectForward(o); c.requestRenderAll(); } };
    const backward = () => { const c = fabricRef.current; const o = c.getActiveObject(); if (o) { c.sendObjectBackwards(o); c.requestRenderAll(); } };
    const setColor = (color) => { const c = fabricRef.current; const o = c.getActiveObject(); if (o) { o.set("fill", color); c.requestRenderAll(); setSelected({ ...o }); } };
    const setFont = (f) => { const c = fabricRef.current; const o = c.getActiveObject(); if (o && o.type === "i-text") { o.set("fontFamily", f); c.requestRenderAll(); } };

    const use = useCallback(async () => {
        setBusy(true);
        try {
            const c = fabricRef.current;
            c.discardActiveObject();
            // hide the print-area guide for export
            c.getObjects().forEach((o) => { if (!o.selectable && !o.evented) o.visible = false; });
            c.requestRenderAll();
            const dataUrl = c.toDataURL({ format: "png", multiplier: 2 });
            c.getObjects().forEach((o) => { if (!o.selectable && !o.evented) o.visible = true; });
            c.requestRenderAll();
            const blob = await (await fetch(dataUrl)).blob();
            const fd = new FormData();
            fd.append("file", new File([blob], `design-${product.slug}.png`, { type: "image/png" }));
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onUse({ preview: data, sources: sourceFiles, previewUrl: dataUrl });
            toast.success("Design saved to your order");
            onClose();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    }, [product, sourceFiles, onUse, onClose]);

    const isText = selected && selected.type === "i-text";

    return (
        <div className="fixed inset-0 z-[80] flex flex-col bg-brand-ink/95 backdrop-blur-sm" data-testid="live-designer">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3 sm:px-6">
                <div className="flex items-center gap-2"><Layers className="text-brand-green" /><span className="font-display text-lg font-bold">Live Designer — {product.name}</span></div>
                <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="designer-close"><X /></button>
            </div>

            <div className="flex flex-1 flex-col overflow-hidden lg:flex-row">
                {/* Tools */}
                <div className="flex flex-wrap gap-2 border-b border-white/10 p-3 lg:w-64 lg:flex-col lg:flex-nowrap lg:border-b-0 lg:border-r lg:p-4">
                    <GhostButton onClick={() => fileRef.current?.click()} className="w-full justify-start py-2.5" data-testid="designer-upload"><Upload size={16} /> Upload art</GhostButton>
                    <GhostButton onClick={addText} className="w-full justify-start py-2.5" data-testid="designer-add-text"><Type size={16} /> Add text</GhostButton>
                    <input ref={fileRef} type="file" accept="image/*,.pdf,.svg" className="hidden" onChange={(e) => e.target.files[0] && addImageFromFile(e.target.files[0])} />

                    <div className="mt-2 hidden h-px w-full bg-white/10 lg:block" />
                    <div className="w-full">
                        <p className="mb-2 hidden font-mono text-[11px] uppercase tracking-widest text-zinc-500 lg:block">Selected object</p>
                        <div className="flex flex-wrap gap-2">
                            <button onClick={dup} disabled={!selected} className="flex-1 rounded-lg border border-white/15 p-2 text-zinc-300 disabled:opacity-30 hover:border-white/30" title="Duplicate" data-testid="designer-duplicate"><Copy size={15} className="mx-auto" /></button>
                            <button onClick={forward} disabled={!selected} className="flex-1 rounded-lg border border-white/15 p-2 text-zinc-300 disabled:opacity-30 hover:border-white/30" title="Forward"><ArrowUp size={15} className="mx-auto" /></button>
                            <button onClick={backward} disabled={!selected} className="flex-1 rounded-lg border border-white/15 p-2 text-zinc-300 disabled:opacity-30 hover:border-white/30" title="Back"><ArrowDown size={15} className="mx-auto" /></button>
                            <button onClick={del} disabled={!selected} className="flex-1 rounded-lg border border-white/15 p-2 text-red-400 disabled:opacity-30 hover:border-red-400/50" title="Delete" data-testid="designer-delete"><Trash2 size={15} className="mx-auto" /></button>
                        </div>
                    </div>

                    <div className="w-full">
                        <p className="mb-2 mt-2 font-mono text-[11px] uppercase tracking-widest text-zinc-500">Color</p>
                        <div className="flex flex-wrap gap-2">
                            {COLORS.map((c) => <button key={c} onClick={() => setColor(c)} disabled={!selected} className="h-7 w-7 rounded-full border border-white/20 disabled:opacity-30" style={{ background: c }} data-testid={`designer-color-${c}`} />)}
                        </div>
                    </div>

                    {isText && (
                        <div className="w-full">
                            <p className="mb-2 mt-2 font-mono text-[11px] uppercase tracking-widest text-zinc-500">Font</p>
                            <select onChange={(e) => setFont(e.target.value)} className="w-full rounded-lg border border-white/15 bg-brand-ink px-3 py-2 text-sm text-white">
                                {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                            </select>
                        </div>
                    )}
                </div>

                {/* Canvas */}
                <div className="flex flex-1 items-center justify-center overflow-auto bg-black/40 p-4">
                    <div className="rounded-xl border border-white/10 shadow-2xl">
                        <canvas ref={canvasElRef} data-testid="designer-canvas" />
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-white/10 px-4 py-3 sm:px-6">
                <p className="hidden text-xs text-zinc-500 sm:block"><Square size={11} className="mr-1 inline text-brand-green" /> Keep art inside the dashed print area. Drag corners to resize & rotate.</p>
                <div className="flex gap-2">
                    <GhostButton onClick={onClose}>Cancel</GhostButton>
                    <GreenButton onClick={use} disabled={busy} data-testid="designer-use"><>{busy ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />} Use This Design</></GreenButton>
                </div>
            </div>
        </div>
    );
}
